import * as utils from '../../utils';
import * as testimCustomToken from '../testimCustomToken';
import WebSocket from 'ws';
import { EventEmitter } from 'events';
import { getLogger } from '../logger';
import { WEBSOCKET_HOST } from '../config';

const logger = getLogger('socket-service');
const WAIT_BETWEEN_RECONNECT_MS = 5000;

class SocketService extends EventEmitter {
    onConnect?: VoidFunction;
    clientId = utils.guid();
    ws: WebSocket | null = null;
    filterMap: { [key: string]: { query: object; id: string; type: string[]; fullDocument: boolean } } = {};
    // @ts-expect-error We are incorrectly overriding the EventEmitter base class
    override listeners: { [listenerName: string]: Array<(data: any) => void> } = {};

    onReconnect(projectId: string) {
        logger.info('test result websocket re-connect');
        setTimeout(() => this.connect(projectId), WAIT_BETWEEN_RECONNECT_MS);
    }

    formatUrl(url: string) {
        if (url.startsWith('http://')) {
            return url.replace('http://', 'ws://');
        }

        if (url.startsWith('https://')) {
            return url.replace('https://', 'wss://');
        }

        return url;
    }

    parseEvent(event: Buffer) {
        try {
            // @ts-expect-error JSON.parse handles buffers as well, the types are not smart enough :(
            return JSON.parse(event);
        } catch (err) {
            logger.error('failed to parse or trigger event', { err });
        }
        return undefined;
    }

    connect(projectId: string) {
        const wsBaseUrl = this.formatUrl(WEBSOCKET_HOST);
        return testimCustomToken.getCustomTokenV3()
            .then(token => new Promise<void>((resolve) => {
                const options = {
                    ...(global.proxyUri && { agent: new global.ProxyAgent!(global.proxyUri) }),
                };
                this.ws = new WebSocket(`${wsBaseUrl}?projectId=${projectId}&clientId=${this.clientId}&token=${token}`, options);

                this.ws.on('open', () => {
                    logger.info('websocket opened');
                    this.reSendAllExistingFilters();
                    this.onConnect?.();
                    return resolve();
                });

                this.ws.on('close', (event) => {
                    logger.info('websocket closed', { event });
                    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
                        this.onReconnect(projectId);
                    }
                });

                this.ws.on('error', (event) => {
                    logger.info('websocket error', { event });
                });

                this.ws.on('message', (event) => {
                    const evObject = this.parseEvent(event as Buffer);
                    if (evObject?.type) {
                        this.emit(evObject.type, evObject.data);
                    }
                });
            }));
    }

    // this is private because our sockets are supposed to be read-only to
    // external consumers and writes need to happen through the REST API
    // this is important since recovery of messages is the case of reconnects is
    // done in the code calling sendMessage and not in sendMessage itself
    sendMessage(msg: any) {
        if (!this.ws) {
            logger.warn('tried to send error when websocket was disconnected');
            return;
        }
        try {
            this.ws.send(JSON.stringify(msg));
        } catch (err) {
            logger.error('failed to stringify message for sending', { err });
        }
    }

    listenOnce<T = any>(eventName: string, eventMatcher: (data: T) => boolean, listener: (data: T) => void) {
        const onEvent = (data: T) => {
            if (eventMatcher(data)) {
                listener(data);
                this.removeListener(eventName, onEvent);
            }
        };

        this.on(eventName, onEvent);
    }

    listenTo<T = any>(key: string, eventName: string | string[], eventMatcher: (data: T) => boolean, listener: (data: T) => void) {
        //TODO - Consider doing key:value event name subscriptions here, routing inside the .on('message' into an event name based on the key and removing the listeners array.
        function fireIfSameMatchEventMatcher(data: T) {
            if (eventMatcher(data)) {
                listener(data);
            }
        }

        const eventNameArray = Array.isArray(eventName) ? eventName : [eventName];
        eventNameArray.forEach(name => {
            this.listeners[`${key}:${name}`] ||= [];
            const _listener = fireIfSameMatchEventMatcher.bind(this);
            this.listeners[`${key}:${name}`].push(_listener);
            this.on(name, _listener);
        });
    }

    reSendAllExistingFilters() {
        Object.keys(this.filterMap).forEach(key => {
            const filter = this.filterMap[key];
            this.sendMessage({ type: 'add-filter', filter });
        });
    }

    addFilter<T extends { id: string } = any>(key: string, query: object, type: string[], returnFullDocument = false) {
        return new Promise<T>(resolve => {
            const filterId = utils.guid();
            const filter = { query, id: filterId, type, fullDocument: returnFullDocument };
            this.listenOnce('add-filter:done', data => data.id === filterId, resolve as (data: T) => void);
            this.sendMessage({ type: 'add-filter', filter });
            this.filterMap[key] = filter;
        });
    }

    removeListeners(key: string, typeArray: string[]) {
        if (Object.keys(this.listeners).length === 0) {
            return;
        }
        typeArray.forEach(name => {
            const listeners = this.listeners[`${key}:${name}`];
            if (listeners) {
                delete this.listeners[`${key}:${name}`];
                listeners.forEach(listener => this.removeListener(name, listener));
            }
        });
    }

    removeFilter(key: string, type: string | string[]) {
        const filter = this.filterMap[key];
        if (!filter) {
            return;
        }

        const typeArray = Array.isArray(type) ? type : [type];
        this.removeListeners(key, typeArray);
        delete this.filterMap[key];
        this.sendMessage({ type: 'remove-filter', filter });
    }
}

export const socketService = new SocketService();
